import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, lt } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import SettingsCentralMappingCategoriesCategory from 'lumen/components/settings/central-mapping/categories/category';
import type { CentralMappingCategory } from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';
import {
  type Breakdown,
  HIDDEN_PARENT_CATEGORY_ID,
} from 'lumen/utils/central-mapping-constants';
import { v4 as uuidv4 } from 'uuid';

import styles from './categories-list.module.css';

interface SettingsCentralMappingCategoriesCategoriesListSignature {
  Args: {
    breakdown: Breakdown;
    categories: CentralMappingCategory[];
    deleteCategory: (category: CentralMappingCategory) => void;
    deleteCategoryWithConfirmationModal: (
      category: CentralMappingCategory,
    ) => void;
    depth: number;
    isLoading?: boolean;
    isReadOnly?: boolean;
    maxDepth: number;
    selectCategory: (category: CentralMappingCategory) => void;
    selectedCategories: CentralMappingCategory[];
    trackCategoryAdded?: (
      level: number,
      eventTrigger: 'new-item-button' | 'add-level-button',
    ) => void;
    trackCategoryRenamed?: (level: number, hasSubCategories: boolean) => void;
  };
}

export default class SettingsCentralMappingCategoriesCategoriesList extends Component<SettingsCentralMappingCategoriesCategoriesListSignature> {
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked isHovered = false;
  @tracked scrollableCategoriesListElement: HTMLElement | null = null;

  get canAddRootCategory(): boolean {
    const { depth, categories, isReadOnly, isLoading } = this.args;

    if (!isLoading && depth === 0 && !isReadOnly) {
      return (
        categories.length === 0 ||
        categories.every((category) => !category.isFixed)
      );
    }

    return false;
  }

  get classesForFooter(): string[] {
    const classes: string[] = ['footer'];

    // This if check is necessary to make this getter react to changes in the categories list.
    if (!this.args.categories) {
      return classes;
    }

    const FOOTER_HEIGHT_IN_PIXELS = 48;

    // If the categories are scrollable, the footer should be sticky.
    const parentHeight =
      (this.scrollableCategoriesListElement?.parentElement?.scrollHeight ?? 0) -
      FOOTER_HEIGHT_IN_PIXELS;

    const scrollHeight =
      this.scrollableCategoriesListElement?.scrollHeight ?? 0;

    if (scrollHeight > parentHeight) {
      classes.push('sticky');
    }

    return classes;
  }

  get hasEmptyNonFixedRootCategory(): boolean {
    const { categories, depth } = this.args;
    return depth === 0 && categories.length === 0;
  }

  get nestedListDepth(): number {
    return this.args.depth + 1;
  }

  get selectedCategory(): CentralMappingCategory | null {
    const { selectedCategories, depth } = this.args;

    return selectedCategories[depth] ?? null;
  }

  get selectedCategoryChildren(): CentralMappingCategory[] {
    return this.selectedCategory?.undeletedChildren ?? [];
  }

  get showFooter(): boolean {
    const { depth, categories } = this.args;

    return (
      !this.args.isReadOnly &&
      this.isHovered &&
      categories.length > 0 &&
      depth > 0
    );
  }

  get sortedCategories(): CentralMappingCategory[] {
    const { depth, categories } = this.args;

    // Level 1 categories are sorted by backend, and should be shown in the order they are returned by the API.
    if (depth === 0) {
      return categories;
    }

    // Sort categories alphabetically.
    // If a category has an empty name, it should be shown at the end of the list.
    return categories.sort((a, b) => {
      if (a.name === '') {
        return 1;
      }

      if (b.name === '') {
        return -1;
      }

      return a.name.localeCompare(b.name);
    });
  }

  get title(): string {
    const { depth } = this.args;

    switch (depth) {
      case 2:
        return this.intl.t('settings.central_mapping.hierarchy.sub_categories');
      case 1:
        return this.intl.t('settings.central_mapping.hierarchy.categories');
      default:
        return this.intl.t('settings.central_mapping.hierarchy.groups');
    }
  }

  willDestroy(): void {
    super.willDestroy();

    this.scrollableCategoriesListElement = null;
  }

  @action addChildCategory(category: CentralMappingCategory): void {
    const { selectedCategories, selectCategory } = this.args;

    const randomId = uuidv4();
    this.store.createRecord<CentralMappingCategory>(
      'central-mapping/category',
      {
        id: randomId,
        name: '',
        isFixed: false,
        parent: category,
        children: [],
      },
    );

    if (!selectedCategories.includes(category)) {
      selectCategory(category);
    }
  }

  @action onClickAddRootCategory(): void {
    const { selectCategory, breakdown } = this.args;
    const randomId = uuidv4();

    const nonFixedNegativeDepthRootCategory =
      this.store.peekRecord<CentralMappingCategory>(
        'central-mapping/category',
        `${HIDDEN_PARENT_CATEGORY_ID}-${breakdown}`,
      );

    const category = this.store.createRecord<CentralMappingCategory>(
      'central-mapping/category',
      {
        id: randomId,
        name: '',
        isFixed: false,
        parent: nonFixedNegativeDepthRootCategory,
        children: [],
        // @ts-expect-error: Incorrect type
        valueType: breakdown,
      },
    );

    selectCategory(category);
  }

  @action onClickAddSiblingCategory(): void {
    const { categories } = this.args;
    const category = categories[0];

    if (!category || !category.parent) {
      return;
    }

    this.addChildCategory(category.parent);

    // Scroll to bottom of the list so the category is visible when the categories are overflowing.
    this.scrollableCategoriesListElement?.scrollTo({
      top: this.scrollableCategoriesListElement.scrollHeight,
      behavior: 'smooth',
    });
  }

  @action onMouseEnter(): void {
    this.isHovered = true;
  }

  @action onMouseLeave(): void {
    this.isHovered = false;
  }

  @action setScrollableCategoriesListElement(element: HTMLElement): void {
    this.scrollableCategoriesListElement = element;
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class={{concat styles.list " " "h-full"}}
      data-test-id="central-mapping-hierarchy-categories-list"
      role="button"
      {{on "mouseenter" this.onMouseEnter}}
      {{on "mouseleave" this.onMouseLeave}}
    >
      <PrismCard @contentLayout="noPadding">
        <:header as |Header|>
          <Header.title>
            <div class="heading-3">{{this.title}}</div>
          </Header.title>
        </:header>

        <:content>
          <div class="flex h-full flex-col overflow-hidden">
            <div
              class="flex flex-col gap-100 overflow-auto p-200 pb-100"
              {{getElement this.setScrollableCategoriesListElement}}
            >
              {{#each this.sortedCategories as |category|}}
                <SettingsCentralMappingCategoriesCategory
                  @addChildCategory={{fn this.addChildCategory category}}
                  @category={{category}}
                  @deleteCategory={{@deleteCategory}}
                  @deleteCategoryWithConfirmationModal={{@deleteCategoryWithConfirmationModal}}
                  @isReadOnly={{@isReadOnly}}
                  @isSelected={{eq category this.selectedCategory}}
                  @maxDepth={{@maxDepth}}
                  @onClick={{@selectCategory}}
                  @selectedCategories={{@selectedCategories}}
                  @trackCategoryAdded={{@trackCategoryAdded}}
                  @trackCategoryRenamed={{@trackCategoryRenamed}}
                />
              {{/each}}
            </div>

            {{#if this.showFooter}}
              <div class={{local styles this.classesForFooter}}>
                <PrismButton
                  @buttonType="secondary"
                  @iconBefore="plus"
                  @label={{t "settings.central_mapping.hierarchy.new_item"}}
                  data-test-id="central-mapping-categories-list-new-category-button"
                  {{on "click" this.onClickAddSiblingCategory}}
                />
              </div>
            {{/if}}

            {{#if this.canAddRootCategory}}
              <div class={{local styles this.classesForFooter}}>
                <PrismButton
                  @buttonType={{if
                    this.hasEmptyNonFixedRootCategory
                    "primary"
                    "secondary"
                  }}
                  @iconBefore="plus"
                  @label={{t "settings.central_mapping.hierarchy.new_item"}}
                  data-test-id="central-mapping-categories-list-new-groups-category-button"
                  {{on "click" this.onClickAddRootCategory}}
                />
              </div>
            {{/if}}
          </div>
        </:content>
      </PrismCard>
    </div>

    {{#if (lt @depth @maxDepth)}}
      <SettingsCentralMappingCategoriesCategoriesList
        @breakdown={{@breakdown}}
        @categories={{this.selectedCategoryChildren}}
        @deleteCategory={{@deleteCategory}}
        @deleteCategoryWithConfirmationModal={{@deleteCategoryWithConfirmationModal}}
        @depth={{this.nestedListDepth}}
        @isReadOnly={{@isReadOnly}}
        @maxDepth={{@maxDepth}}
        @selectCategory={{@selectCategory}}
        @selectedCategories={{@selectedCategories}}
        @trackCategoryAdded={{@trackCategoryAdded}}
        @trackCategoryRenamed={{@trackCategoryRenamed}}
      />
    {{/if}}
  </template>
}
